import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  SelectProps,
  Stack,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconUser } from "@tabler/icons-react"
import { LabelWithTooltip, Select } from "@tfg/ui"
import { sortBy, uniqBy } from "lodash"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { ContactDto, findContactsQueryKey, useFindContacts } from "~/gen/api"
import useContactForm from "~/pages/SupplierPortal/Contacts/hooks/useContactForm"
import { ContactDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactDrawer.tsx"

interface Props extends Partial<SelectProps> {
  contactId: string | null
  onContactSelected: (contactId: string) => void
  userId: string
  isAdmin: boolean
  informationRequestId?: number
  tooltip?: string
  isReinforcedTraceability?: boolean
}

const getIsCurrentUser = (contact: ContactDto, userId: string) => {
  return contact.userId === userId
}

const getContactLabel = (contact: ContactDto, currentUserId: string) => {
  return `${contact.name}${getIsCurrentUser(contact, currentUserId) ? " (you)" : ` (${contact.email})`}`
}

export const ContactSelect = ({
  contactId,
  onContactSelected,
  disabled,
  userId,
  isAdmin,
  informationRequestId,
  tooltip,
  isReinforcedTraceability,
}: Props) => {
  const { t } = useTranslation(["traceability"])
  const [modalOpen, modalActions] = useDisclosure(false)
  const { contact, handleContact, reset } = useContactForm({
    onContactCreated: (contactId) => {
      onContactSelected(contactId)
      queryClient.invalidateQueries({
        queryKey: findContactsQueryKey({
          createdById: userId,
        }),
      })
    },
  })

  const { data, isPending: isListLoading } = useFindContacts({
    createdById: userId,
    informationRequestId,
  })
  const selectOptions = useMemo(() => {
    if (!data) return []

    const uniqueContacts = uniqBy(data.data.contacts, "email")
    const orderedContacts = sortBy(uniqueContacts, (contact) => contact.name)

    return orderedContacts.map((contact) => ({
      value: contact.id?.toString(),
      label: getContactLabel(contact, userId),
      contact,
    }))
  }, [data, userId])
  const contactUser = selectOptions.find(
    (option) => option.value === contactId?.toString()
  )
  const reinforcedError =
    isReinforcedTraceability && contactUser?.contact?.userId === userId
      ? t("errors.reinforcedTraceability")
      : undefined
  const error = (!contactId && !disabled) || reinforcedError

  return (
    <>
      {isListLoading ? (
        <Select isLoading />
      ) : (
        <Select
          data={selectOptions}
          value={contactId?.toString() ?? null}
          limit={20}
          creatable
          searchable
          leftSection={<IconUser size={16} />}
          copyableValue={isAdmin ? "Contact Id" : undefined}
          isAdmin={isAdmin}
          label={
            <LabelWithTooltip
              justify="space-between"
              label={t("contactSelect.labels.contact")}
              hoverHelperText={
                tooltip && tooltip.length > 0 ? tooltip : undefined
              }
            />
          }
          onChange={(value) => {
            value ? onContactSelected(value) : null
          }}
          placeholder={t("contactSelect.placeholders.contact")}
          createLabel={t("contactSelect.labels.addNewContact")}
          onCreate={() => {
            modalActions.open()
            return null
          }}
          errorOnTouch={!reinforcedError}
          customRender={({
            option,
            checked,
          }: ComboboxLikeRenderOptionInput<
            ComboboxItem & { contact?: ContactDto }
          >) => {
            const contact = option.contact
            if (!contact) {
              return null
            }

            return (
              <Stack
                px="var(--tfg-molecule-padding-x)"
                py="var(--tfg-molecule-padding-y)"
                gap={0}
                w="100%"
                data-checked={checked ? "true" : undefined}
              >
                <Text fw={700}>
                  {contact.name}{" "}
                  {getIsCurrentUser(contact, userId)
                    ? `(${t("contactSelect.labels.you")})`
                    : ""}
                </Text>
                <Text>{contact.email}</Text>
              </Stack>
            )
          }}
          error={error}
          disabled={disabled}
        />
      )}

      {modalOpen && (
        <ContactDrawer
          opened={modalOpen}
          setModalOpened={{
            open: modalActions.open,
            close: () => {
              modalActions.close()
              reset()
              queryClient.invalidateQueries({
                queryKey: findContactsQueryKey({
                  createdById: userId,
                }),
              })
            },
            toggle: modalActions.toggle,
          }}
          handleSupplierMapping={handleContact}
          supplierMapping={contact}
          isUpdate={false}
        />
      )}
    </>
  )
}
